import {Controller} from "@hotwired/stimulus"

/**
 * Bag Checkout Form Validator (Native)
 *
 * Uses native browser Constraint Validation API for simplicity and reliability.
 * No external libraries, no detached element issues, works perfectly in Safari.
 */
export default class extends Controller {
    connect() {
        //console.log('BagCheckoutFormValidator connected (native validation)');

        // Track if submit button was clicked (for Safari compatibility)
        this.submitButtonClicked = false;
        this.isSubmitting = false;
        this.submitButton = null;
        this.originalSubmitButtonHTML = null;

        // Setup native validation
        this.initializeNativeValidation();
    }

    initializeNativeValidation() {
        const form = this.element;
        if (!form) {
            console.error('Bag checkout form element not found');
            return;
        }

        // Prevent default HTML5 validation UI (we'll style it ourselves)
        form.setAttribute('novalidate', 'novalidate');

        // Track submit button clicks for Safari compatibility
        this.submitButton = form.querySelector('[type="submit"]');
        if (this.submitButton) {
            this.originalSubmitButtonHTML = this.submitButton.innerHTML;
            this.submitButton.addEventListener('click', (e) => {
                this.submitButtonClicked = true;
                //console.log('Submit button clicked - flag set');
            });
        }

        // Handle form submission
        form.addEventListener('submit', (e) => {
            e.preventDefault();

            // Only actually submit if submit button was clicked
            if (!this.submitButtonClicked) {
                return;
            }

            if (this.isSubmitting) {
                return;
            }

            // Validate all visible fields
            const isValid = this.validateVisibleFields();

            if (!isValid) {
                this.submitButtonClicked = false;
                return;
            }

            this.lockSubmitButton();

            // Turnstile token must be present before posting; the server
            // rejects the order without it. Usually ready long before the
            // shopper reaches Place Order, so this rarely waits.
            const turnstile = form._turnstile;
            if (turnstile && !turnstile.hasToken()) {
                turnstile.waitForToken().then((token) => {
                    if (token) {
                        this.tokenizeAndSubmit(form);
                    } else {
                        this.submitButtonClicked = false;
                        this.unlockSubmitButton();
                        turnstile.showPending();
                    }
                });
                return;
            }

            this.tokenizeAndSubmit(form);
        });

        // Store validation helper on form for other controllers to use
        form._nativeValidator = this;

        // Add real-time validation on blur
        this.setupRealtimeValidation(form);

        //console.log('BagCheckout Form Validator initialized successfully (native)');
    }

    tokenizeAndSubmit(form) {
        // Gate on Braintree Hosted Fields tokenization for card payments.
        // The braintree controller exposes needsTokenize() / tokenize() and
        // writes the nonce into the hidden order[payment_method_nonce] field.
        const braintree = this.application?.getControllerForElementAndIdentifier(form, 'bag--braintree');
        if (braintree && braintree.needsTokenize()) {
            braintree.tokenize().then((ok) => {
                this.submitButtonClicked = false;
                if (ok) {
                    this.submitWithTurnstile(form);
                } else {
                    this.unlockSubmitButton();
                }
            }).catch((error) => {
                console.error('Braintree tokenization failed:', error);
                this.submitButtonClicked = false;
                this.unlockSubmitButton();
            });
            return;
        }

        this.submitButtonClicked = false;
        this.submitWithTurnstile(form);
    }

    // Final gate right before the POST. The Turnstile token is single-use and
    // expires after 300s; the earlier check can go stale while Braintree
    // tokenizes, so re-verify here and wait for the auto-refreshed token.
    submitWithTurnstile(form) {
        const turnstile = form._turnstile;
        if (!turnstile || turnstile.hasToken()) {
            form.submit();
            return;
        }

        turnstile.waitForToken().then((token) => {
            if (token) {
                form.submit();
            } else {
                this.unlockSubmitButton();
                turnstile.showPending();
            }
        });
    }

    lockSubmitButton() {
        this.isSubmitting = true;

        if (!this.submitButton) {
            return;
        }

        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...';
    }

    unlockSubmitButton() {
        this.isSubmitting = false;

        if (!this.submitButton) {
            return;
        }

        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
        if (this.originalSubmitButtonHTML !== null) {
            this.submitButton.innerHTML = this.originalSubmitButtonHTML;
        }
    }

    /**
     * Normalize validation error messages
     * @param {string} message - The browser's validation message
     * @returns {string} Normalized message
     */
    normalizeErrorMessage(message) {
        // Replace common browser messages with cleaner versions
        if (!message || message === '') return 'Required Field';
        if (message.toLowerCase().includes('fill out this field')) return 'Required Field';
        if (message.toLowerCase().includes('please fill')) return 'Required Field';
        if (message.toLowerCase().includes('select an item')) return 'Required Field';
        return message;
    }

    safeCheckValidity(field) {
        try {
            return {
                isValid: field.checkValidity(),
                errorMessage: this.normalizeErrorMessage(field.validationMessage)
            };
        } catch (error) {
            console.error('Validation failed for field:', field.name || field.id, error);
            return {
                isValid: false,
                errorMessage: field.title || field.dataset.errorMessage || 'Please check this field'
            };
        }
    }

    /**
     * Validate all visible fields in the form
     * @returns {boolean} True if all visible fields are valid
     */
    validateVisibleFields() {
        const form = this.element;
        let allValid = true;
        let firstInvalidField = null;

        // Get all input/select/textarea elements with validation
        const fields = form.querySelectorAll('input, select, textarea');

        fields.forEach(field => {
            // Skip fields that should not be validated
            if (this.shouldSkipField(field)) {
                // Remove any validation styling from hidden fields
                field.classList.remove('is-invalid', 'is-valid');
                return;
            }

            // Cleartext card fields are gone (replaced by Braintree Hosted
            // Fields iframes); rely on native HTML5 validation for the rest.
            const { isValid, errorMessage } = this.safeCheckValidity(field);

            if (!isValid) {
                field.classList.add('is-invalid');
                allValid = false;

                // Track first invalid field for focus
                if (!firstInvalidField) {
                    firstInvalidField = field;
                }

                // Show error message
                this.showFieldError(field, errorMessage);
            } else {
                field.classList.remove('is-invalid');
                this.hideFieldError(field);
            }
        });

        // Focus on first invalid field
        if (firstInvalidField) {
            firstInvalidField.focus();
            // Scroll to field
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return allValid;
    }

    /**
     * Validate a single field
     * @param {string} fieldName - Name attribute of the field
     * @returns {boolean} True if field is valid
     */
    validateField(fieldName) {
        const field = this.element.querySelector(`[name="${fieldName}"]`);

        if (!field || this.shouldSkipField(field)) {
            return true;
        }

        const { isValid, errorMessage } = this.safeCheckValidity(field);

        if (!isValid) {
            field.classList.add('is-invalid');
            this.showFieldError(field, errorMessage);
            return false;
        } else {
            field.classList.remove('is-invalid');
            this.hideFieldError(field);
            return true;
        }
    }

    /**
     * Determine if a field should be skipped during validation
     * @param {HTMLElement} field
     * @returns {boolean}
     */
    shouldSkipField(field) {
        // Skip if no name attribute
        if (!field.name) {
            return true;
        }

        // Skip disabled fields
        if (field.disabled) {
            return true;
        }

        // Skip fields in inactive sections
        const section = field.closest('.checkout-section');
        if (section) {
            const isActive = section.classList.contains('active');
            const isCompleted = section.classList.contains('completed');
            if (!isActive && !isCompleted) {
                return true;
            }
        }

        // Skip billing fields if billing form is hidden
        if (field.name.includes('bill_')) {
            const billingForm = field.closest('#billing-address-form');
            if (billingForm) {
                const isHidden = window.getComputedStyle(billingForm).display === 'none';
                if (isHidden) {
                    return true;
                }
            }
        }

        // Skip fields in hidden payment method sections
        const paymentContent = field.closest('.payment-method-content');
        if (paymentContent) {
            const isHidden = window.getComputedStyle(paymentContent).display === 'none';
            if (isHidden) {
                return true;
            }
        }

        // Skip if explicitly marked
        if (field.dataset.validate === 'false') {
            return true;
        }

        return false;
    }

    /**
     * Show error message for a field
     * @param {HTMLElement} field
     * @param {string} customMessage - Optional custom error message
     */
    showFieldError(field, customMessage = null) {
        const container = field.closest('.fv-row') || field.parentElement;
        let feedback = container.querySelector('.invalid-feedback');

        // Generate unique ID for error message
        const errorId = `error-${field.name || field.id || Math.random().toString(36).substr(2, 9)}`;

        if (!feedback) {
            feedback = document.createElement('div');
            feedback.className = 'invalid-feedback';
            feedback.id = errorId;
            feedback.style.display = 'block';
            feedback.setAttribute('role', 'alert');
            container.appendChild(feedback);
        } else {
            feedback.id = errorId;
        }

        // Use provided message, or field's dataset, or browser validation message
        const message = customMessage || field.dataset.errorMessage || this.normalizeErrorMessage(field.validationMessage) || 'Required Field';
        feedback.textContent = message;
        feedback.style.display = 'block';

        // Associate error message with field using aria-describedby
        field.setAttribute('aria-describedby', errorId);
        field.setAttribute('aria-invalid', 'true');
    }

    /**
     * Hide error message for a field
     * @param {HTMLElement} field
     */
    hideFieldError(field) {
        const container = field.closest('.fv-row') || field.parentElement;
        const feedback = container.querySelector('.invalid-feedback');

        if (feedback) {
            feedback.style.display = 'none';
        }

        // Remove ARIA attributes
        field.removeAttribute('aria-describedby');
        field.removeAttribute('aria-invalid');
    }

    /**
     * Setup real-time validation on blur
     * @param {HTMLFormElement} form
     */
    setupRealtimeValidation(form) {
        const fields = form.querySelectorAll('input, select, textarea');

        fields.forEach(field => {
            // Validate on blur
            field.addEventListener('blur', () => {
                if (this.shouldSkipField(field)) return;

                const { isValid, errorMessage } = this.safeCheckValidity(field);

                if (!isValid) {
                    field.classList.add('is-invalid');
                    this.showFieldError(field, errorMessage);
                } else {
                    field.classList.remove('is-invalid');
                    this.hideFieldError(field);
                }
            });

            // Clear error on input (while typing)
            field.addEventListener('input', () => {
                if (!field.classList.contains('is-invalid')) return;

                const { isValid, errorMessage } = this.safeCheckValidity(field);

                if (isValid) {
                    field.classList.remove('is-invalid');
                    this.hideFieldError(field);
                } else {
                    this.showFieldError(field, errorMessage);
                }
            });
        });
    }

    /**
     * Reset validation state
     */
    resetValidation() {
        const fields = this.element.querySelectorAll('input, select, textarea');
        fields.forEach(field => {
            field.classList.remove('is-invalid');
            this.hideFieldError(field);
        });
    }

    disconnect() {
        // Cleanup if needed
    }
}
